import { reactive, computed } from 'vue'
import axios from 'axios'

// Global state for notifications
const state = reactive({
    toasts: [],
    notifications: [],
    unreadCount: 0,
    loading: false,
    initialized: false
})

export function useNotifications() {
    // Fetch notifications from API
    const fetchNotifications = async () => {
        if (state.loading) return

        state.loading = true
        try {
            const response = await axios.get('/api/notifications', {
                params: { per_page: 50 }
            })

            state.notifications = (response.data.notifications?.data || []).map(n => ({
                ...n,
                notificationId: n.id,
                read: n.is_read,
                timestamp: n.created_at
            }))
            state.unreadCount = response.data.unread_count || 0
            state.initialized = true
        } catch (error) {
            console.error('Error fetching notifications:', error)
        } finally {
            state.loading = false
        }
    }

    // Initialize notifications on first use
    const initNotifications = () => {
        if (!state.initialized && !state.loading) {
            fetchNotifications()
        }
    }

    // Add a toast notification (local only - for immediate feedback)
    const addToast = (options) => {
        const toast = {
            id: Date.now(),
            type: options.type || 'info',
            title: options.title || '',
            message: options.message || '',
            duration: options.duration !== undefined ? options.duration : 5000,
            persistent: options.persistent || false,
            timestamp: new Date()
        }

        state.toasts.push(toast)

        // Auto remove after duration (if not persistent)
        if (toast.duration > 0 && !toast.persistent) {
            setTimeout(() => {
                removeToast(toast.id)
            }, toast.duration)
        }

        return toast.id
    }

    // Remove a toast notification
    const removeToast = (id) => {
        const index = state.toasts.findIndex(t => t.id === id)
        if (index > -1) {
            state.toasts.splice(index, 1)
        }
    }

    // Mark notification as read (API call)
    const markAsRead = async (id) => {
        try {
            await axios.patch(`/api/notifications/${id}/read`)

            const notification = state.notifications.find(n => n.notificationId === id || n.id === id)
            if (notification) {
                notification.read = true
                notification.is_read = true
            }
            state.unreadCount = Math.max(0, state.unreadCount - 1)
        } catch (error) {
            console.error('Error marking notification as read:', error)
        }
    }

    // Mark notification as unread (API call)
    const markAsUnread = async (id) => {
        try {
            await axios.patch(`/api/notifications/${id}/unread`)

            const notification = state.notifications.find(n => n.notificationId === id || n.id === id)
            if (notification) {
                notification.read = false
                notification.is_read = false
            }
            state.unreadCount++
        } catch (error) {
            console.error('Error marking notification as unread:', error)
        }
    }

    // Mark all as read (API call)
    const markAllAsRead = async () => {
        try {
            await axios.post('/api/notifications/mark-all-read')

            state.notifications.forEach(n => {
                n.read = true
                n.is_read = true
            })
            state.unreadCount = 0
        } catch (error) {
            console.error('Error marking all as read:', error)
        }
    }

    // Remove notification (API call)
    const removeNotification = async (id) => {
        try {
            await axios.delete(`/api/notifications/${id}`)

            const index = state.notifications.findIndex(n => n.notificationId === id || n.id === id)
            if (index > -1) {
                const wasUnread = !state.notifications[index].read
                state.notifications.splice(index, 1)
                if (wasUnread) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1)
                }
            }
        } catch (error) {
            console.error('Error removing notification:', error)
        }
    }

    // Clear all notifications (API call)
    const clearAllNotifications = async () => {
        try {
            await axios.delete('/api/notifications/clear-all')

            state.notifications = []
            state.unreadCount = 0
        } catch (error) {
            console.error('Error clearing notifications:', error)
        }
    }

    // Convenience methods for different toast types
    const success = (message, options = {}) => {
        return addToast({
            type: 'success',
            message,
            ...options
        })
    }

    const error = (message, options = {}) => {
        return addToast({
            type: 'error',
            message,
            persistent: true,
            ...options
        })
    }

    const warning = (message, options = {}) => {
        return addToast({
            type: 'warning',
            message,
            ...options
        })
    }

    const info = (message, options = {}) => {
        return addToast({
            type: 'info',
            message,
            ...options
        })
    }

    // Computed properties
    const unreadCountComputed = computed(() => state.unreadCount)
    const hasUnread = computed(() => state.unreadCount > 0)

    return {
        // State
        toasts: computed(() => state.toasts),
        notifications: computed(() => state.notifications),
        unreadCount: unreadCountComputed,
        hasUnread,
        loading: computed(() => state.loading),

        // Methods
        initNotifications,
        fetchNotifications,
        addToast,
        removeToast,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,

        // Convenience methods
        success,
        error,
        warning,
        info
    }
}
